import { memo, useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Textarea,
  Separator,
} from '@flote/ui';
import { Loader2, MapPin, Search, Navigation } from 'lucide-react';
import { useGooglePlaces, type ParsedAddress } from '@flote/app-core';
import type { CreateDestinationRequest } from '@flote/contracts/http';

export type DestinationFormData = CreateDestinationRequest;

interface DestinationFormProps {
  onSubmit: (data: DestinationFormData) => Promise<void>;
  isProcessing?: boolean;
}

export const DestinationForm = memo<DestinationFormProps>(({
  onSubmit,
  isProcessing = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Address search field (full formatted address)
  const [addressSearch, setAddressSearch] = useState('');

  // Parsed address fields
  const [streetNumber, setStreetNumber] = useState('');
  const [streetAddress, setStreetAddress] = useState('');
  const [apartment, setApartment] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('USA');

  // Optional coordinates (auto-filled from geocoding or manual)
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [showCoordinates, setShowCoordinates] = useState(false);

  // Destination name (classification)
  const [destinationName, setDestinationName] = useState('');

  // Notes
  const [notes, setNotes] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle address selection from Google Places autocomplete
  const handlePlaceSelect = useCallback((address: ParsedAddress) => {
    setAddressSearch(address.formattedAddress);
    setStreetNumber(address.number);
    setStreetAddress(address.street);
    setApartment(address.apartment);
    setCity(address.city);
    setState(address.state);
    setPostalCode(address.postalCode);
    setCountry(address.country || 'USA');

    // Clear any address-related errors when auto-filled
    setErrors((prev) => {
      const next = { ...prev };
      delete next.addressSearch;
      delete next.streetNumber;
      delete next.streetAddress;
      delete next.city;
      delete next.country;
      return next;
    });
  }, []);

  // Google Places autocomplete hook
  const { inputRef: addressSearchRef } = useGooglePlaces(
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    { onPlaceSelect: handlePlaceSelect }
  );

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Onfleet requires: number, street, city, country for parsed addresses
    if (!streetNumber.trim()) {
      newErrors.streetNumber = 'Street number is required';
    }
    if (!streetAddress.trim()) {
      newErrors.streetAddress = 'Street name is required';
    }
    if (!city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!country.trim()) {
      newErrors.country = 'Country is required';
    }

    // Validate coordinates if provided
    if (latitude.trim() || longitude.trim()) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);

      if (latitude.trim() && (isNaN(lat) || lat < -90 || lat > 90)) {
        newErrors.latitude = 'Latitude must be between -90 and 90';
      }
      if (longitude.trim() && (isNaN(lng) || lng < -180 || lng > 180)) {
        newErrors.longitude = 'Longitude must be between -180 and 180';
      }
      if ((latitude.trim() && !longitude.trim()) || (!latitude.trim() && longitude.trim())) {
        newErrors.coordinates = 'Both latitude and longitude are required if providing coordinates';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildDestinationRequest = (): DestinationFormData => {
    const request: DestinationFormData = {
      address: {
        name: destinationName.trim() || undefined,
        number: streetNumber.trim() || undefined,
        street: streetAddress.trim() || undefined,
        apartment: apartment.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim().toUpperCase() || undefined,
        postalCode: postalCode.trim() || undefined,
        country: country.trim() || undefined,
      },
      notes: notes.trim() || undefined,
    };

    // Add coordinates if both are provided
    if (latitude.trim() && longitude.trim()) {
      request.location = {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
      };
    }

    return request;
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const destinationRequest = buildDestinationRequest();
      await onSubmit(destinationRequest);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    destinationName, streetNumber, streetAddress, apartment, city, state, postalCode, country,
    latitude, longitude, notes, onSubmit
  ]);

  const processing = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Destination Address Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Destination Address
        </div>

        {/* Address Search with Autocomplete */}
        <div className="space-y-2">
          <Label htmlFor="addressSearch">Search Address</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={addressSearchRef}
              id="addressSearch"
              placeholder="Start typing to search for an address..."
              value={addressSearch}
              onChange={(e) => setAddressSearch(e.target.value)}
              disabled={processing}
              className={`pl-9 ${errors.addressSearch ? 'border-destructive' : ''}`}
              autoComplete="off"
            />
          </div>
          {errors.addressSearch && (
            <p className="text-xs text-destructive">{errors.addressSearch}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Search for an address to auto-fill the fields below, or enter manually
          </p>
        </div>

        {/* Destination Name (Classification) */}
        <div className="space-y-2">
          <Label htmlFor="destinationName">Destination Name</Label>
          <Input
            id="destinationName"
            placeholder="e.g., Warehouse A, Main Office, Customer HQ"
            value={destinationName}
            onChange={(e) => setDestinationName(e.target.value)}
            disabled={processing}
          />
          <p className="text-xs text-muted-foreground">
            Optional name to classify this destination (e.g., building name, location nickname)
          </p>
        </div>

        {/* Parsed Address Fields */}
        <div className="grid gap-4 sm:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="streetNumber">Street Number *</Label>
            <Input
              id="streetNumber"
              placeholder="123"
              value={streetNumber}
              onChange={(e) => setStreetNumber(e.target.value)}
              disabled={processing}
              className={errors.streetNumber ? 'border-destructive' : ''}
            />
            {errors.streetNumber && (
              <p className="text-xs text-destructive">{errors.streetNumber}</p>
            )}
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="streetAddress">Street Name *</Label>
            <Input
              id="streetAddress"
              placeholder="Main Street"
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              disabled={processing}
              className={errors.streetAddress ? 'border-destructive' : ''}
            />
            {errors.streetAddress && (
              <p className="text-xs text-destructive">{errors.streetAddress}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="apartment">Apt / Suite</Label>
            <Input
              id="apartment"
              placeholder="Unit 4B"
              value={apartment}
              onChange={(e) => setApartment(e.target.value)}
              disabled={processing}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              placeholder="Los Angeles"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={processing}
              className={errors.city ? 'border-destructive' : ''}
            />
            {errors.city && (
              <p className="text-xs text-destructive">{errors.city}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Input
              id="state"
              placeholder="CA"
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase())}
              disabled={processing}
              maxLength={2}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="postalCode">Postal Code</Label>
            <Input
              id="postalCode"
              placeholder="90001"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              disabled={processing}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Input
              id="country"
              placeholder="USA"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={processing}
              className={errors.country ? 'border-destructive' : ''}
            />
            {errors.country && (
              <p className="text-xs text-destructive">{errors.country}</p>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* Coordinates Section (Optional) */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Navigation className="h-4 w-4 text-muted-foreground" />
            GPS Coordinates
            <span className="text-xs font-normal text-muted-foreground">(optional)</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowCoordinates(!showCoordinates)}
          >
            {showCoordinates ? 'Hide' : 'Show'}
          </Button>
        </div>

        {showCoordinates && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              If not provided, Onfleet will automatically geocode the address. Provide coordinates for precise location control.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  placeholder="37.7875728"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  disabled={processing}
                  className={errors.latitude || errors.coordinates ? 'border-destructive' : ''}
                />
                {errors.latitude && (
                  <p className="text-xs text-destructive">{errors.latitude}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  placeholder="-122.3965731"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  disabled={processing}
                  className={errors.longitude || errors.coordinates ? 'border-destructive' : ''}
                />
                {errors.longitude && (
                  <p className="text-xs text-destructive">{errors.longitude}</p>
                )}
              </div>
            </div>

            {errors.coordinates && (
              <p className="text-xs text-destructive">{errors.coordinates}</p>
            )}
          </div>
        )}
      </div>

      <Separator />

      {/* Notes Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Delivery instructions, gate codes, landmarks, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={processing}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            Notes about this destination that will be shown to drivers
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing} className="w-full">
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating Destination...
            </>
          ) : (
            'Create Destination'
          )}
        </Button>
      </div>
    </form>
  );
});

DestinationForm.displayName = 'DestinationForm';
